/**
 * Local persistence — best-run stats and consent banner state.
 *
 * Deferred Supabase: a remote save makes sense once there's an account
 * system. For a prototype, localStorage is plenty and keeps the loop tight.
 */

const KEY_PROFILE = 'wanderborne.profile.v1';
const KEY_CONSENT = 'wanderborne.consent.v1';

export interface Profile {
  bestDepth: number;
  bestKills: number;
  bestGold: number;
  totalRuns: number;
  lastRun?: {
    depth: number;
    kills: number;
    gold: number;
    durationSeconds: number;
    cause: string;
    endedAt: number;
  };
}

const DEFAULT_PROFILE: Profile = {
  bestDepth: 0,
  bestKills: 0,
  bestGold: 0,
  totalRuns: 0,
};

export function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(KEY_PROFILE);
    if (!raw) return { ...DEFAULT_PROFILE };
    return { ...DEFAULT_PROFILE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PROFILE };
  }
}

export function saveProfile(p: Profile): void {
  try { localStorage.setItem(KEY_PROFILE, JSON.stringify(p)); } catch { /* ignore */ }
}

export function recordRunEnd(stats: {
  depth: number;
  kills: number;
  gold: number;
  durationSeconds: number;
  cause: string;
}): Profile {
  const p = loadProfile();
  p.totalRuns++;
  p.bestDepth = Math.max(p.bestDepth, stats.depth);
  p.bestKills = Math.max(p.bestKills, stats.kills);
  p.bestGold  = Math.max(p.bestGold,  stats.gold);
  p.lastRun = { ...stats, endedAt: Date.now() };
  saveProfile(p);
  return p;
}

export function getConsent(): boolean | null {
  try {
    const raw = localStorage.getItem(KEY_CONSENT);
    if (raw === 'yes') return true;
    if (raw === 'no')  return false;
    return null;
  } catch { return null; }
}

export function setConsent(value: boolean): void {
  try { localStorage.setItem(KEY_CONSENT, value ? 'yes' : 'no'); } catch { /* ignore */ }
}
